import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_EN_FULL = ['January', 'February', 'March', 'April', 'May', 'Jun', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_ZH = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const WEEKDAYS_EN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEKDAYS_ZH = ['日', '一', '二', '三', '四', '五', '六'];

/**
 * DatePickerInput - A user-friendly date picker with calendar dropdown.
 * Drop-in replacement for <input type="date" />
 *
 * Props:
 *   value       - ISO date string (YYYY-MM-DD) or empty
 *   onChange     - called with ISO date string or synthetic event { target: { name, value } }
 *   name        - input name (for form compatibility)
 *   min         - minimum date (YYYY-MM-DD)
 *   max         - maximum date (YYYY-MM-DD)
 *   disabled    - boolean
 *   style       - container style override
 *   inputStyle  - input element style override
 *   placeholder - placeholder text
 *   required    - boolean
 */
export default function DatePickerInput({
  value, onChange, name, min, max, disabled, style, inputStyle,
  placeholder, required, className, id, ...rest
}) {
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const calRef = useRef(null);

  // Parse value
  const parsed = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(parsed ? parsed.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed ? parsed.getMonth() : new Date().getMonth());
  const [showYearSelect, setShowYearSelect] = useState(false);

  // Keep view synced when value changes externally
  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]); // eslint-disable-line

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position calendar above if near bottom
  const [dropUp, setDropUp] = useState(false);
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropUp(rect.bottom + 320 > window.innerHeight);
    }
  }, [open]);

  const fireChange = useCallback((isoStr) => {
    if (onChange) {
      // Support both direct value and event-style callbacks
      if (typeof onChange === 'function') {
        // Try event-style first
        const syntheticEvent = { target: { name: name || '', value: isoStr } };
        onChange(syntheticEvent);
      }
    }
  }, [onChange, name]);

  const handleSelect = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const iso = `${viewYear}-${m}-${d}`;
    fireChange(iso);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    fireChange('');
    setOpen(false);
  };

  // Calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

  const isDisabled = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const iso = `${viewYear}-${m}-${d}`;
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  };

  const isSelected = (day) => {
    if (!parsed) return false;
    return parsed.getFullYear() === viewYear && parsed.getMonth() === viewMonth && parsed.getDate() === day;
  };

  const isToday = (day) => {
    const now = new Date();
    return now.getFullYear() === viewYear && now.getMonth() === viewMonth && now.getDate() === day;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  // Display value (friendly, shown when the input isn't being typed in).
  const displayValue = parsed
    ? isZh
      ? `${parsed.getFullYear()}年${parsed.getMonth() + 1}月${parsed.getDate()}日`
      : `${MONTHS_EN[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()}`
    : '';

  // Editable typing state — lets the user EITHER pick from the calendar OR
  // type the date directly. Recognized formats: YYYY-MM-DD, YYYY/MM/DD,
  // MM/DD/YYYY, YYYY年M月D日.
  const [typing, setTyping] = useState(false);
  const [typedRaw, setTypedRaw] = useState('');

  const parseTyped = (s) => {
    s = String(s || '').trim();
    if (!s) return '';
    const iso = (y, mo, d) => {
      const dt = new Date(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}T00:00:00`);
      if (isNaN(dt)) return null;
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
    if (m) return iso(m[1], m[2], m[3]);
    m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
    if (m) return iso(m[3], m[1], m[2]);           // assume MM/DD/YYYY
    m = /^(\d{4})年(\d{1,2})月(\d{1,2})日?$/.exec(s);
    if (m) return iso(m[1], m[2], m[3]);
    return null;                                   // not yet a complete date
  };

  const emit = (v) => {
    if (!onChange) return;
    if (onChange.length >= 1) {
      try { onChange({ target: { name, value: v } }); return; } catch (_) { /* fall through */ }
    }
    onChange(v);
  };

  const handleInputChange = (e) => {
    const s = e.target.value;
    setTypedRaw(s);
    if (s === '') { emit(''); return; }
    const parsedIso = parseTyped(s);
    if (parsedIso) emit(parsedIso);                // commit live while typing once it parses
  };

  const handleBlur = () => {
    setTyping(false);
    if (typedRaw && !parseTyped(typedRaw) && !value) {
      // typed something that never resolved → discard
      setTypedRaw('');
    }
  };

  // Year range for selector
  const minYear = min ? parseInt(min.split('-')[0]) : 1920;
  const maxYear = max ? parseInt(max.split('-')[0]) : new Date().getFullYear() + 10;

  const months = isZh ? MONTHS_ZH : MONTHS_EN;
  const weekdays = isZh ? WEEKDAYS_ZH : WEEKDAYS_EN;

  // Styles
  const S = {
    container: { position: 'relative', display: 'inline-block', ...style },
    input: {
      padding: '8px 32px 8px 10px', fontSize: 13, border: '1px solid #D6D0C7', borderRadius: 2,
      background: disabled ? '#EFEAE2' : '#FFFDF9', cursor: disabled ? 'default' : 'pointer',
      outline: 'none', fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif", color: '#111111',
      width: '100%', boxSizing: 'border-box',
      ...inputStyle
    },
    icon: {
      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
      pointerEvents: 'none', color: '#6E685F', fontSize: 14
    },
    calendar: {
      position: 'absolute', [dropUp ? 'bottom' : 'top']: '100%',
      left: 0, marginTop: dropUp ? 0 : 4, marginBottom: dropUp ? 4 : 0,
      zIndex: 9999, background: '#FFFDF9', borderRadius: 2, boxShadow: 'none',
      border: '1px solid #D6D0C7', padding: 12, width: 280, fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif",
      animation: 'fadeIn 0.15s ease'
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8
    },
    navBtn: {
      width: 28, height: 28, border: 'none', borderRadius: 2, background: '#EFEAE2',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, color: '#3A3835', transition: 'background 0.15s'
    },
    monthLabel: {
      fontSize: 13, fontWeight: 600, color: '#111111', cursor: 'pointer',
      padding: '2px 8px', borderRadius: 2, transition: 'background 0.15s'
    },
    weekdayRow: {
      display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, marginBottom: 4
    },
    weekday: {
      textAlign: 'center', fontSize: 10, fontWeight: 600, color: '#6E685F', padding: '4px 0'
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 },
    day: (selected, today, dis, isOtherMonth) => ({
      width: 36, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, borderRadius: 2, cursor: dis ? 'default' : 'pointer',
      fontWeight: selected ? 700 : today ? 600 : 400,
      background: selected ? '#111111' : 'transparent',
      color: dis ? '#D6D0C7' : selected ? '#FFFDF9' : isOtherMonth ? '#C9C3BA' : today ? '#8A6D3B' : '#3A3835',
      border: today && !selected ? '1px solid #B8975A' : '1px solid transparent',
      transition: 'all 0.1s'
    }),
    clearBtn: {
      position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
      cursor: 'pointer', color: '#6E685F', fontSize: 12, border: 'none', background: 'none',
      padding: 2, display: 'flex', alignItems: 'center'
    },
    yearGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4,
      maxHeight: 200, overflowY: 'auto', padding: 4
    },
    yearBtn: (selected) => ({
      padding: '6px 4px', fontSize: 12, border: 'none', borderRadius: 2,
      cursor: 'pointer', textAlign: 'center',
      background: selected ? '#111111' : '#F6F3EE',
      color: selected ? '#FFFDF9' : '#3A3835',
      fontWeight: selected ? 700 : 400
    })
  };

  return (
    <div ref={containerRef} style={S.container} className={className}>
      <input
        id={id}
        value={typing ? typedRaw : displayValue}
        placeholder={placeholder || (isZh ? '点击选择或直接输入 (YYYY-MM-DD)' : 'Pick or type (YYYY-MM-DD)')}
        onChange={handleInputChange}
        onFocus={() => {
          if (disabled) return;
          setTyping(true);
          setTypedRaw(value || '');
          setOpen(true);
        }}
        onBlur={handleBlur}
        onClick={() => !disabled && setOpen(true)}
        style={{ ...S.input, cursor: disabled ? 'default' : 'text' }}
        disabled={disabled}
        required={required}
        autoComplete="off"
        inputMode="numeric"
        {...rest}
      />
      <span style={S.icon}></span>
      {value && !disabled && (
        <button type="button" onClick={handleClear} style={S.clearBtn} title={isZh ? '清除' : 'Clear'}>
          &times;
        </button>
      )}

      {open && (
        <div ref={calRef} style={S.calendar} onClick={e => e.stopPropagation()}>
          {showYearSelect ? (
            <>
              <div style={S.header}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{isZh ? '选择年份' : 'Select Year'}</span>
                <button type="button" style={S.navBtn} onClick={() => setShowYearSelect(false)}>&times;</button>
              </div>
              <div style={S.yearGrid}>
                {Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i).map(y => (
                  <button type="button" key={y} style={S.yearBtn(y === viewYear)}
                    onClick={() => { setViewYear(y); setShowYearSelect(false); }}>
                    {y}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={S.header}>
                <button type="button" style={S.navBtn} onClick={prevMonth}
                  onMouseEnter={e => e.target.style.background = '#E4DFD7'}
                  onMouseLeave={e => e.target.style.background = '#EFEAE2'}>
                  &#8249;
                </button>
                <span style={S.monthLabel} onClick={() => setShowYearSelect(true)}
                  onMouseEnter={e => e.target.style.background = '#EFEAE2'}
                  onMouseLeave={e => e.target.style.background = 'transparent'}>
                  {isZh ? `${viewYear}年 ${months[viewMonth]}` : `${MONTHS_EN_FULL[viewMonth]} ${viewYear}`}
                </span>
                <button type="button" style={S.navBtn} onClick={nextMonth}
                  onMouseEnter={e => e.target.style.background = '#E4DFD7'}
                  onMouseLeave={e => e.target.style.background = '#EFEAE2'}>
                  &#8250;
                </button>
              </div>
              <div style={S.weekdayRow}>
                {weekdays.map((wd, i) => <div key={i} style={S.weekday}>{wd}</div>)}
              </div>
              <div style={S.grid}>
                {/* Previous month trailing days */}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`p${i}`} style={S.day(false, false, true, true)}>
                    {prevMonthDays - firstDay + 1 + i}
                  </div>
                ))}
                {/* Current month days */}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const sel = isSelected(day);
                  const td = isToday(day);
                  const dis = isDisabled(day);
                  return (
                    <div key={day} style={S.day(sel, td, dis, false)}
                      onClick={() => !dis && handleSelect(day)}
                      onMouseEnter={e => { if (!sel && !dis) e.target.style.background = '#EFEAE2'; }}
                      onMouseLeave={e => { if (!sel && !dis) e.target.style.background = 'transparent'; }}>
                      {day}
                    </div>
                  );
                })}
                {/* Next month leading days */}
                {Array.from({ length: (7 - ((firstDay + daysInMonth) % 7)) % 7 }, (_, i) => (
                  <div key={`n${i}`} style={S.day(false, false, true, true)}>
                    {i + 1}
                  </div>
                ))}
              </div>
              {/* Today button */}
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button type="button" style={{
                  border: 'none', background: 'none', color: '#111111', fontSize: 11,
                  cursor: 'pointer', fontWeight: 600, fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif"
                }} onClick={() => {
                  const now = new Date();
                  setViewYear(now.getFullYear());
                  setViewMonth(now.getMonth());
                  handleSelect(now.getDate());
                }}>
                  {isZh ? '今天' : 'Today'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
